import type { Book, Release, ReleasesResponse } from '../types';
import { isRecord } from './objectHelpers';
import { getReleaseFormats } from './releaseFormats';

function normalizeMatchText(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['’`]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function collectNormalizedStrings(values: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const normalizedValues: string[] = [];

  for (const value of values) {
    if (!value) continue;
    const normalized = normalizeMatchText(value);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    normalizedValues.push(normalized);
  }

  return normalizedValues;
}

function getLocalizedTitleValues(raw: unknown): string[] {
  if (!isRecord(raw) || Array.isArray(raw)) {
    return [];
  }

  const values: string[] = [];
  for (const value of Object.values(raw)) {
    if (typeof value === 'string' && value.trim()) {
      values.push(value);
    }
  }
  return values;
}

function splitAuthorString(author: string): string[] {
  return author
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

export function getBookTitleCandidates(
  uiBook: Book | null,
  responseBook: ReleasesResponse['book'] | undefined,
): string[] {
  return collectNormalizedStrings([
    responseBook?.search_title,
    responseBook?.title,
    ...getLocalizedTitleValues(responseBook?.titles_by_language),
    uiBook?.search_title,
    uiBook?.title,
    ...getLocalizedTitleValues(uiBook?.titles_by_language),
  ]);
}

export function getBookAuthorCandidates(
  uiBook: Book | null,
  responseBook: ReleasesResponse['book'] | undefined,
): string[] {
  const responseAuthors = responseBook?.authors ?? [];
  const uiAuthors = uiBook?.authors ?? [];
  const uiAuthorParts = uiBook?.author ? splitAuthorString(uiBook.author) : [];
  return collectNormalizedStrings([
    responseBook?.search_author,
    ...responseAuthors,
    uiBook?.search_author,
    ...uiAuthors,
    ...uiAuthorParts,
  ]);
}

function getReleaseAuthorForMatch(release: Release): string | null {
  const rawAuthor = release.extra?.author;
  if (typeof rawAuthor !== 'string') {
    return null;
  }

  const normalized = normalizeMatchText(rawAuthor);
  return normalized || null;
}

function getReleaseTitleCandidates(release: Release): string[] {
  const bookTitle = release.extra?.book_title;
  return collectNormalizedStrings([
    release.title,
    typeof bookTitle === 'string' ? bookTitle : undefined,
  ]);
}

function hasAuthorMatch(release: Release, authorCandidates: string[]): boolean {
  if (authorCandidates.length === 0) {
    return false;
  }

  const releaseAuthor = getReleaseAuthorForMatch(release);
  if (!releaseAuthor) {
    return false;
  }

  const releaseTokens = new Set(releaseAuthor.split(' ').filter(Boolean));
  return authorCandidates.some((candidate) => {
    const candidateTokens = candidate.split(' ').filter(Boolean);
    return candidateTokens.length > 0 && candidateTokens.every((token) => releaseTokens.has(token));
  });
}

const STOP_WORDS = new Set([
  'a',
  'an',
  'the',
  'and',
  'or',
  'of',
  'in',
  'to',
  'for',
  'on',
  'at',
  'by',
  'is',
]);

function removeStopWords(text: string): string {
  return text
    .split(' ')
    .filter((w) => !STOP_WORDS.has(w))
    .join(' ');
}

function getTitleMatchScore(title: string, titleCandidate: string): number {
  const normalizedTitle = normalizeMatchText(title);
  if (!normalizedTitle || !titleCandidate) {
    return 0;
  }

  // Exact match on full normalized strings (highest score)
  if (normalizedTitle === titleCandidate) {
    return 10000;
  }

  // Also check with stop words stripped for substring comparisons
  const strippedTitle = removeStopWords(normalizedTitle);
  const strippedCandidate = removeStopWords(titleCandidate);

  let score = 0;

  if (normalizedTitle.startsWith(titleCandidate) || strippedTitle.startsWith(strippedCandidate)) {
    score += 6000;
  } else if (
    normalizedTitle.includes(titleCandidate) ||
    strippedTitle.includes(strippedCandidate)
  ) {
    score += 3000;
  }

  // Token overlap uses stop-word-stripped versions so only meaningful words are compared
  const candidateTokens = strippedCandidate.split(' ').filter((token) => token.length >= 3);
  if (candidateTokens.length > 0) {
    const titleTokens = new Set(strippedTitle.split(' '));
    const matchedTokens = candidateTokens.filter((token) => titleTokens.has(token)).length;
    score += Math.round((matchedTokens / candidateTokens.length) * 2500);
  }

  // Prefer closer-length titles when match quality is otherwise similar.
  score -= Math.min(Math.abs(normalizedTitle.length - titleCandidate.length), 100);

  return score;
}

const EBOOK_FORMAT_SCORES = new Map<string, number>([
  ['epub', 5200],
  ['azw3', 3000],
  ['mobi', 2400],
  ['fb2', 1800],
  ['pdf', 400],
]);

function normalizeLanguage(
  language: string,
  languageNormalizer?: Map<string, string>,
): string {
  const normalized = language.trim().toLowerCase();
  return languageNormalizer?.get(normalized) ?? normalized;
}

function releaseMatchesPreferredLanguages(
  releaseLanguage: string,
  preferredLanguages: string[],
  languageNormalizer?: Map<string, string>,
): boolean {
  const preferred = new Set(
    preferredLanguages.map((language) => normalizeLanguage(language, languageNormalizer)),
  );
  return releaseLanguage
    .split(/[,/+&]/)
    .map((language) => normalizeLanguage(language, languageNormalizer))
    .filter(Boolean)
    .every((language) => preferred.has(language));
}

export function getReleaseRecommendationScore(
  release: Release,
  preferredLanguages: string[] = [],
  languageNormalizer?: Map<string, string>,
): number {
  const formats = getReleaseFormats(release);
  const formatScore = formats.reduce(
    (best, format) => Math.max(best, EBOOK_FORMAT_SCORES.get(format) ?? 0),
    0,
  );

  let score = formatScore;

  // Prefer an explicitly correct language over unknown language metadata. A
  // known mismatch receives a strong penalty as a safeguard when the caller is
  // showing more than one configured language.
  if (release.language) {
    if (preferredLanguages.length === 0) {
      score += 300;
    } else if (
      releaseMatchesPreferredLanguages(
        release.language,
        preferredLanguages,
        languageNormalizer,
      )
    ) {
      score += 1200;
    } else {
      score -= 2500;
    }
  }

  if (release.protocol === 'nzb' || release.protocol === 'http') {
    score += 600;
  } else if (release.protocol === 'torrent') {
    if (release.seeders == null) {
      score -= 800;
    } else if (release.seeders <= 0) {
      score -= 5000;
    } else {
      score += Math.min(Math.round(Math.log2(release.seeders + 1) * 200), 1000);
    }
  }

  if (release.extra?.freeleech === true) score += 150;
  if (release.extra?.vip === true) score -= 400;

  if (release.size_bytes != null) {
    if (release.size_bytes < 50 * 1024) {
      score -= 3000;
    } else if (release.size_bytes <= 250 * 1024 * 1024) {
      score += 150;
    } else {
      score -= 150;
    }
  }

  return score;
}

export function sortReleasesByBookMatch(
  releases: Release[],
  titleCandidates: string[],
  authorCandidates: string[],
  preferredLanguages: string[] = [],
  languageNormalizer?: Map<string, string>,
): Release[] {
  const sorted = releases
    .map((release, index) => ({
      release,
      index,
      score: (() => {
        const releaseTitles = getReleaseTitleCandidates(release);
        const titleScore = releaseTitles.reduce(
          (bestReleaseTitle, releaseTitle) =>
            Math.max(
              bestReleaseTitle,
              titleCandidates.reduce(
                (bestBookTitle, bookTitle) =>
                  Math.max(bestBookTitle, getTitleMatchScore(releaseTitle, bookTitle)),
                0,
              ),
            ),
          0,
        );
        return (
          titleScore +
          (hasAuthorMatch(release, authorCandidates) ? 1500 : 0) +
          getReleaseRecommendationScore(release, preferredLanguages, languageNormalizer)
        );
      })(),
    }))
    .toSorted((a, b) => {
      const scoreDiff = b.score - a.score;
      if (scoreDiff !== 0) {
        return scoreDiff;
      }
      return a.index - b.index;
    })
    .map(({ release }) => release);

  return sorted.map((release, index) => {
    const extra = release.extra ?? {};
    const rawFlags = Array.isArray(extra.indexer_flags) ? extra.indexer_flags : [];
    const flags = rawFlags.filter(
      (flag): flag is string => typeof flag === 'string' && !flag.startsWith('Recommended'),
    );

    if (index === 0) {
      const label = getReleaseFormats(release).includes('epub')
        ? 'Recommended EPUB'
        : 'Recommended';
      flags.unshift(label);
    }

    const nextExtra = { ...extra };
    delete nextExtra.recommendation;
    delete nextExtra.recommendation_score;

    return {
      ...release,
      extra: {
        ...nextExtra,
        indexer_flags: flags,
        ...(index === 0 ? { recommendation: flags[0] } : {}),
      },
    };
  });
}
